import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ShieldCheck, Lock, Zap, Eye, Star } from 'lucide-react';
// --- Geo-based pricing (Simulated for this specific product) ---
interface PriceData {
  oldPrice: string;
  newPrice: string;
  savings: string;
  ctaPrice: string;
}
// Defaulting all to the requested $29 USD equivalent for simplicity as per prompt instructions
// In a real scenario, we'd calculate these.
const PRICES: Record<string, PriceData> = {
  DEFAULT: {
    oldPrice: '$158 USD',
    newPrice: '$6.39',
    savings: 'AHORRAS $151.61',
    ctaPrice: '$6.39 USD'
  }
};
function useGeoPrice() {
  // For this transformation, we stick to the prompt's explicit pricing
  return PRICES.DEFAULT;
}
// --- Live viewer count ---
function useLiveViewers(initial = 142) {
  const [count, setCount] = useState(initial);
  useEffect(() => {
    const interval = setInterval(() => {
      setCount(prev => prev + Math.floor(Math.random() * 5) - 2);
    }, 3500);
    return () => clearInterval(interval);
  }, []);
  return count;
}
// --- Countdown Timer ---
function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState(14 * 60 + 22);
  useEffect(() => {
    if (timeLeft <= 0) return;
    const interval = setInterval(() => setTimeLeft(t => Math.max(0, t - 1)), 1000);
    return () => clearInterval(interval);
  }, [timeLeft]);
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return <div className="flex items-center justify-center gap-3 bg-[var(--red-emphasis)]/15 border border-[var(--red-emphasis)]/30 rounded-lg px-4 py-3 mb-6" data-id="element-64">
      <Zap className="w-4 h-4 text-[var(--red-emphasis)] fill-current flex-shrink-0" data-id="element-65" />
      <span className="text-sm text-[var(--text-primary)]" data-id="element-66">
        El precio sube cuando el contador llegue a cero:
      </span>
      <div className="flex items-center gap-1 font-mono font-bold text-[var(--red-emphasis)] text-lg" data-id="element-67">
        <span className="bg-[var(--red-emphasis)]/20 px-2 py-0.5 rounded" data-id="element-68">
          {pad(minutes)}
        </span>
        <span className="animate-pulse" data-id="element-69">:</span>
        <span className="bg-[var(--red-emphasis)]/20 px-2 py-0.5 rounded" data-id="element-70">
          {pad(seconds)}
        </span>
      </div>
    </div>;
}
export function CTASection() {
  const price = useGeoPrice();
  const viewers = useLiveViewers();
  const bonuses = [{
    label: '📘 Producto Principal: Ebook "¿Por Qué a Mí, Dios?"',
    value: '$97'
  }, {
    label: '🌙 BONUS #1: "Oraciones de las 3:00 AM" (Para el pánico nocturno)',
    value: '$27'
  }, {
    label: '📓 BONUS #2: "Journal del Elegido" (Plantilla diaria)',
    value: '$17'
  }, {
    label: '🆘 BONUS #3: "Kit SOS: Paz en 5 minutos" (Crisis inmediatas)',
    value: '$17'
  }];
  const handleBuy = () => {
    if (typeof (window as any).fbq === 'function') {
      ;
      (window as any).fbq('track', 'AddToCart', {
        content_name: 'Por Que A Mi Dios - Manual',
        content_type: 'product',
        value: 6.39,
        currency: 'USD'
      });
    }
    // Placeholder link
    window.open('https://www.simplementemejor.com/cart/51716691231000:1', '_blank');
  };
  return <section id="cta-section" className="py-14 px-6 bg-[var(--bg-deep)] relative overflow-hidden" data-id="element-71">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none" style={{
      background: 'radial-gradient(circle, rgba(201,168,76,0.06) 0%, transparent 70%)'
    }} data-id="element-72" />

      <div className="max-w-[580px] mx-auto relative z-10" data-id="element-73">
        {/* Live viewers + scarcity */}
        <div className="flex flex-col gap-3 mb-6" data-id="element-74">
          <motion.div initial={{
          opacity: 0,
          y: -10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="flex items-center justify-center gap-2 bg-[var(--red-emphasis)]/8 border border-[var(--red-emphasis)]/20 rounded-lg px-4 py-3 text-sm shadow-[0_0_15px_rgba(0,0,0,0.05)]" data-id="element-75">
            <Eye className="w-4 h-4 text-[var(--accent)]" data-id="element-76" />
            <span className="text-[var(--text-muted)]" data-id="element-77">
              <AnimatePresence mode="wait" data-id="element-78">
                <motion.strong key={viewers} initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 4
              }} className="text-[var(--text-heading)]" data-id="element-79">
                  {viewers}
                </motion.strong>
              </AnimatePresence>{' '}
              personas están viendo esta oferta ahora
            </span>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="bg-[var(--bg-card)] border border-[var(--accent)]/25 p-6 md:p-8 rounded-xl text-center shadow-[0_4px_40px_rgba(0,0,0,0.08)]" data-id="element-80">
          <p className="text-[var(--accent)] uppercase tracking-widest text-xs font-bold mb-3" data-id="element-81">
            Oferta Especial de Lanzamiento
          </p>
          <h2 className="text-3xl md:text-4xl font-serif text-[var(--text-heading)] mb-2" data-id="element-82">
            Sal del desierto con propósito
          </h2>
          <p className="text-[var(--text-primary)] mb-5 font-light text-sm" data-id="element-83">
            "El elegido no es el que nunca cayó. Es el que entendió por qué tuvo
            que caer."
          </p>

          <CountdownTimer data-id="element-84" />

          {/* Value stack */}
          <div className="bg-[var(--bg-elevated)] rounded-lg p-4 mb-5 text-left space-y-2 border border-[var(--border-subtle)]" data-id="element-85">
            <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-widest mb-2 text-center" data-id="element-86">
              Lo que recibes hoy:
            </p>
            {bonuses.map((item, i) => <div key={i} className="flex items-center justify-between gap-3 text-sm" data-id="element-87">
                <div className="flex items-center gap-2 text-[var(--text-primary)]" data-id="element-88">
                  <Check className="w-4 h-4 text-[var(--accent)] flex-shrink-0" data-id="element-89" />
                  <span data-id="element-90">{item.label}</span>
                </div>
                <span className="text-[var(--text-muted)] line-through text-xs flex-shrink-0" data-id="element-91">
                  {item.value}
                </span>
              </div>)}
            <div className="border-t border-[var(--bg-elevated)] pt-3 mt-3 flex items-center justify-between" data-id="element-92">
              <span className="text-[var(--text-muted)] text-xs" data-id="element-93">
                Valor total real:
              </span>
              <span className="text-[var(--text-muted)] line-through text-sm" data-id="element-94">
                $158 USD
              </span>
            </div>
          </div>

          {/* Geo price */}
          <div className="flex items-baseline justify-center gap-3 mb-2" data-id="element-95">
            <span className="text-xl text-[var(--text-muted)] line-through opacity-60" data-id="element-96">
              {price.oldPrice}
            </span>
            <div className="flex flex-col items-center" data-id="element-97">
              <span className="text-5xl font-serif font-bold text-[var(--accent)]" data-id="element-98">
                {price.newPrice}
              </span>
              <span className="text-[var(--text-muted)] text-xs uppercase tracking-widest" data-id="element-99">
                Pago único
              </span>
            </div>
          </div>
          <div className="inline-block bg-gradient-to-r from-[var(--accent)] to-[#dfc06a] text-[#110608] px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-5" data-id="element-100">
            {price.savings}
          </div>

          {/* CTA Button */}
          <motion.button onClick={handleBuy} whileHover={{
          scale: 1.03
        }} whileTap={{
          scale: 0.97
        }} animate={{
          boxShadow: ['0 0 0 0 rgba(201,168,76,0)', '0 0 40px 12px rgba(201,168,76,0.4)', '0 0 0 0 rgba(201,168,76,0)']
        }} transition={{
          boxShadow: {
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }
        }} className="relative w-full py-5 bg-gradient-to-r from-[var(--accent)] to-[#dfc06a] text-[#110608] font-extrabold text-xl rounded-lg shadow-lg overflow-hidden group cursor-pointer mb-4" data-id="element-101">
            <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/30 to-transparent" data-id="element-102" />
            <span className="relative flex items-center justify-center gap-2" data-id="element-103">
              <Lock className="w-5 h-5" data-id="element-104" />
              SÍ, QUIERO MI COPIA AHORA
            </span>
          </motion.button>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-4 text-[10px] text-[var(--text-muted)] mb-4 flex-wrap" data-id="element-105">
            <span className="flex items-center gap-1" data-id="element-106">
              <Lock className="w-3 h-3" data-id="element-107" /> Pago Seguro
            </span>
            <span className="opacity-30" data-id="element-108">|</span>
            <span className="flex items-center gap-1" data-id="element-109">
              <Zap className="w-3 h-3" data-id="element-110" /> Descarga Inmediata
            </span>
            <span className="opacity-30" data-id="element-111">|</span>
            <span className="flex items-center gap-1" data-id="element-112">
              <ShieldCheck className="w-3 h-3" data-id="element-113" /> Garantía 7 días
            </span>
          </div>

          {/* Guarantee */}
          <div className="border border-[var(--accent)]/20 bg-[var(--accent)]/5 rounded-lg p-5" data-id="element-114">
            <div className="flex items-center justify-center gap-2 mb-2" data-id="element-115">
              <ShieldCheck className="w-5 h-5 text-[var(--accent)]" data-id="element-116" />
              <h4 className="text-[var(--text-heading)] font-serif text-sm font-bold" data-id="element-117">
                Garantía de 7 días — Sin Preguntas
              </h4>
            </div>
            <p className="text-xs text-[var(--text-muted)] leading-relaxed" data-id="element-118">
              Si en 7 días de lectura no sientes que este material habla
              directamente a tu situación, te devolvemos cada centavo. Sin
              formularios complicados. Sin preguntas.
            </p>
          </div>

          {/* Book image */}
          <div className="mt-5 flex justify-center" data-id="element-119">
            <img src="https://cdn.magicpatterns.com/uploads/1L9eAkStwSG5sdVN9P27cF/cover_1.png" alt="¿Por Qué a Mí, Dios?" className="w-28 opacity-90 hover:opacity-100 transition-opacity rounded-md shadow-lg" data-id="element-120" />
          </div>
        </motion.div>
      </div>
    </section>;
}